import { useEffect, useState } from "react";
import { FiPause, FiPlay, FiSquare } from "react-icons/fi";
import { Link } from "react-router-dom";

import { translateMessage } from "@/i18n";
import {
  elapsedMilliseconds,
  pauseTimer,
  readTimer,
  READING_TIMER_CHANGED_EVENT,
  resumeTimer,
  writeTimer,
  type ReadingTimerState,
} from "@/utils/readingTimer";

interface GlobalReadingTimerProps {
  userId: number;
}

function formatElapsed(milliseconds: number): string {
  const seconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return [hours, minutes, remainder]
    .map((value: number) => String(value).padStart(2, "0"))
    .join(":");
}

export function GlobalReadingTimer({ userId }: GlobalReadingTimerProps) {
  const [timer, setTimer] = useState<ReadingTimerState | null>(() => readTimer(userId));
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const syncTimer = (): void => setTimer(readTimer(userId));
    syncTimer();
    window.addEventListener(READING_TIMER_CHANGED_EVENT, syncTimer);
    window.addEventListener("storage", syncTimer);
    return () => {
      window.removeEventListener(READING_TIMER_CHANGED_EVENT, syncTimer);
      window.removeEventListener("storage", syncTimer);
    };
  }, [userId]);

  useEffect(() => {
    if (!timer?.runningSince) return;
    const interval = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(interval);
  }, [timer?.runningSince]);

  if (!timer) return null;

  const togglePause = (): void => {
    const current = new Date();
    setNow(current);
    writeTimer(
      timer.runningSince ? pauseTimer(timer, current) : resumeTimer(timer, current)
    );
  };

  return (
    <aside
      aria-label={translateMessage("reading.GlobalTimer.activeSession")}
      className="fixed inset-x-2 bottom-[calc(4rem+env(safe-area-inset-bottom)+0.5rem)] z-30 flex min-h-14 items-center gap-2 rounded-xl border border-accent/30 bg-ink px-3 py-2 text-surface shadow-soft md:inset-x-auto md:bottom-5 md:right-5 md:w-[min(28rem,calc(100vw-2.5rem))]"
    >
      <Link className="min-w-0 flex-1 text-surface" to="/reading">
        <p className="truncate text-xs font-semibold text-surface/75">
          {timer.bookTitle}
        </p>
        <p
          aria-label={translateMessage("reading.ReadingTimer.elapsedTime")}
          className="font-mono text-lg tabular-nums"
          role="timer"
        >
          {formatElapsed(elapsedMilliseconds(timer, now))}
        </p>
      </Link>
      <button
        aria-label={translateMessage(
          timer.runningSince
            ? "reading.ReadingTimer.pause"
            : "reading.ReadingTimer.resume"
        )}
        className="flex h-11 w-11 items-center justify-center rounded-lg bg-surface/10 text-surface hover:bg-surface/20 focus:outline-none focus:ring-2 focus:ring-surface/60"
        onClick={togglePause}
        type="button"
      >
        {timer.runningSince ? <FiPause /> : <FiPlay />}
      </button>
      <Link
        aria-label={translateMessage("reading.GlobalTimer.finish")}
        className="flex h-11 items-center gap-2 rounded-lg bg-highlight px-3 text-sm font-semibold text-ink hover:bg-highlight/90"
        to="/reading#finish-reading-session"
      >
        <FiSquare aria-hidden="true" />
        <span className="hidden min-[390px]:inline">
          {translateMessage("reading.ReadingTimer.finish")}
        </span>
      </Link>
    </aside>
  );
}
